import { SubscriptionApiType, SubscriptionDiscountsApiType } from '../../types/api/SubscriptionApiType';

/**
 * 運用メモ（重要）
 * - Shopify 側で配送料の閾値や地域別送料を変更した場合は、このファイルの定数も更新すること。
 * - 更新対象:
 *   - FREE_SHIPPING_THRESHOLD（送料無料の閾値）
 *   - BASE_SHIPPING_FEE（地域別送料）
 *   - HOKKAIDO_CODES / OKINAWA_CODES（地域判定コード）
 * - 本実装は「常温便のみ」を対象にしている。冷蔵便の送料ロジックは含めない。
 */
const FREE_SHIPPING_THRESHOLD = 8000;

const BASE_SHIPPING_FEE = {
  default: 500,
  hokkaido: 800,
  okinawa: 1500,
} as const;

const HOKKAIDO_CODES = new Set(['JP-01', '01', '北海道']);
const OKINAWA_CODES = new Set(['JP-47', '47', '沖縄', '沖縄県']);

/**
 * 都道府県コードの比較用に、null/undefinedを空文字へ寄せて前後空白を除去する。
 */
function normalizeProvinceKey(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

/**
 * 割引率の入力形式を 0〜1 の倍率に正規化する。
 * - 10 なら 0.10
 * - 0.1 なら 0.10
 */
export function normalizeDiscountPercentage(percentage: number | null | undefined) {
  if (percentage === null || percentage === undefined || Number.isNaN(percentage)) return null;
  if (percentage < 0) return 0;
  // percentage の解釈ゆれ（10 と 0.1）を両方扱う。
  return percentage > 1 ? percentage / 100 : percentage;
}

/**
 * 商品対象クーポン（LINE_ITEM）の値引き額を算出する。
 * 会計整合を優先し、amount / percentage を先に使用する。
 */
function resolveLineItemDiscountAmount(
  discount: SubscriptionDiscountsApiType,
  subtotalPrice: number
): number {
  // 商品値引きは会計整合を優先し、定義値（amount / percentage）を先に使う。
  if (discount.amount !== null) {
    return Math.max(0, Number(discount.amount));
  }

  const normalized = normalizeDiscountPercentage(discount.percentage);
  if (normalized !== null) {
    return Math.max(0, Math.floor(subtotalPrice * normalized));
  }

  if (discount.actualAmount !== null) {
    return Math.max(0, Number(discount.actualAmount));
  }

  return 0;
}

/**
 * 送料対象クーポン（SHIPPING_LINE）の値引き額を算出する。
 * - 同期完了時: Shopify が返した実値（actualAmount）を優先する。
 * - 同期中: 更新前の値が残っている可能性がある actualAmount は使わず、
 *   amount / percentage を優先して計算する。
 */
function resolveShippingDiscountAmount(
  discount: SubscriptionDiscountsApiType,
  shippingPrice: number,
  isSyncingSubscription: boolean
): number {
  if (!isSyncingSubscription && discount.actualAmount !== null) {
    return Math.max(0, Number(discount.actualAmount));
  }
  if (discount.amount !== null) {
    return Math.max(0, Number(discount.amount));
  }

  const normalized = normalizeDiscountPercentage(discount.percentage);
  if (normalized === null) return 0;

  return Math.max(0, Math.floor(shippingPrice * normalized));
}

/**
 * クーポン値引き額を「商品対象」と「送料対象」に分解して返す。
 * 返り値:
 * - total: 合計値引き額
 * - lineItem: 商品対象クーポンの値引き額
 * - shippingLine: 送料対象クーポンの値引き額
 */
export function calculateCouponDiscountBreakdown(
  subscription: SubscriptionApiType | undefined,
  subtotalPrice: number,
  shippingPrice: number
) {
  if (!subscription) {
    return { total: 0, lineItem: 0, shippingLine: 0 };
  }

  let lineItem = 0;
  let shippingLine = 0;

  for (const discount of subscription.subscriptionDiscounts) {
    if (!discount?.coupon) continue;

    if (discount.targetType === 'SHIPPING_LINE') {
      const remainingShipping = Math.max(0, shippingPrice - shippingLine);
      const resolved = resolveShippingDiscountAmount(
        discount,
        shippingPrice,
        subscription.isSyncingSubscription
      );
      // SHIPPING_LINE かつ値引き額が未確定（actualAmount/amount/percentage が全て null）の間は
      // 「送料無料クーポン」を想定して送料全額を値引き候補にする。
      // ただし値引きは現在の送料を上限とする（送料0円時は0円のまま）。
      const fallbackShippingDiscount =
        discount.actualAmount === null && discount.amount === null && discount.percentage === null
          ? remainingShipping
          : resolved;
      shippingLine += Math.min(remainingShipping, fallbackShippingDiscount);
      continue;
    }

    lineItem += resolveLineItemDiscountAmount(discount, subtotalPrice);
  }

  return {
    total: lineItem + shippingLine,
    lineItem,
    shippingLine,
  };
}

/**
 * 配送先都道府県コードから、基本送料（閾値未満時）を返す。
 */
function resolveBaseShippingFeeByProvince(
  deliveryProvinceCode: string | null | undefined
) {
  const code = normalizeProvinceKey(deliveryProvinceCode).toUpperCase();

  if (HOKKAIDO_CODES.has(code)) {
    return BASE_SHIPPING_FEE.hokkaido;
  }
  if (OKINAWA_CODES.has(code)) {
    return BASE_SHIPPING_FEE.okinawa;
  }
  return BASE_SHIPPING_FEE.default;
}

/**
 * 同期中に使う送料の推定値を返す。
 * 手順:
 * 1. 商品小計を算出
 * 2. 商品対象クーポン値引き後の金額で送料無料閾値を判定
 * 3. 閾値未満なら都道府県別の送料を返す
 */
export function calculateEstimatedShippingPrice(subscription: SubscriptionApiType): number {
  // 送料自動計算の対象外は、既存の送料をそのまま優先する。
  if (subscription.excludeFromAutoCalculateDeliveryPrice) {
    return subscription.deliveryPriceAmount ?? 0;
  }

  const subtotal = subscription.subscriptionLines.reduce((sum, line) => {
    return sum + line.currentPriceAmount * line.quantity;
  }, 0);

  const lineItemDiscount = calculateCouponDiscountBreakdown(subscription, subtotal, 0).lineItem;
  const subtotalAfterDiscount = Math.max(0, subtotal - lineItemDiscount);

  if (subtotalAfterDiscount >= FREE_SHIPPING_THRESHOLD) {
    return 0;
  }

  return resolveBaseShippingFeeByProvince(subscription.deliveryProvinceCode);
}

/**
 * 画面表示用の送料を返す。
 * - 同期完了時: API の deliveryPriceAmount
 * - 同期中: フロント推定値
 */
export function calculateDisplayShippingPrice(subscription: SubscriptionApiType): number {
  if (!subscription.isSyncingSubscription) {
    return subscription.deliveryPriceAmount ?? 0;
  }
  return calculateEstimatedShippingPrice(subscription);
}

